import { useContext, useEffect, useState } from "react";
import {
	Box,
	Button,
	Divider,
	Typography,
	Modal,
	IconButton,
} from "@mui/material";
import CityContext from "../../contexts/CityContext";
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import * as geoprocessor from "@arcgis/core/rest/geoprocessor";
import { urbanReport } from "../../reports/urbanReport";
import CloseIcon from "@mui/icons-material/Close";
import React from "react";
import JobInfo from "@arcgis/core/rest/support/JobInfo";
import {
	gpPDF_URL,
	incomeLabels,
	ageLabels,
	demoLabels,
	dataFields,
} from "../../constants";
import { chartOptions } from "../chartOptions";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { trackEvent } from "../../hooks/useAnalytics";

const modalStyle = {
	position: "absolute" as "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	width: 400,
	bgcolor: "background.paper",
	boxShadow: 24,
	p: 4,
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
};

export default function UrbanAreaInfoForPanel({ urbanarea }: any) {
	ChartJS.register(
		CategoryScale,
		LinearScale,
		BarElement,
		Title,
		Tooltip,
		Legend
	);

	const { city, urban } = useContext(CityContext);

	// The `open` state and the two following functions are used to control whether or not the modal is displayed
	const [open, setOpen] = React.useState(false);
	const handleOpen = () => setOpen(true);
	const handleClose = () => setOpen(false);

	// These three state variables store data related to the GP Service request.
	// jobMessage is the current message returned from the GP Service while it is in progress and displayed to the user
	// pdfUrl is returned when the service is complete and is the location of the resulting report
	// gpSuccess handles whether the report request was successful or not
	const [jobMessage, setJobMessage] = React.useState<string>("");
	const [pdfUrl, setPdfUrl] = React.useState<string>("");
	const [gpSuccess, setGPSuccess] = React.useState(false);

	// the variables below contains configuration options used to render the Chart.js charts for a selected urban area.
	// TODO: These variables should be compared with the ones in constants.ts used to render the city area park and this file should potentially reference those instead. This would make it easier to update the values in a single place
	const colors = [
		"#E7F4B8",
		"#BEDA58",
		"#5DBA54",
		"#1D8363",
		"#57C5F8",
		"#2EA0D4",
		"#1989BC",
	];

	const demographicsInServiceArea: any = [
		{
			displayName: "White*",
			value: "SUM_WHITE_SVC",
			total: "SUM_UA_WHITE",
			amount: 0,
		},
		{
			displayName: "Black*",
			value: "SUM_BLACK_SVC",
			total: "SUM_UA_BLACK",
			amount: 0,
		},
		{
			displayName: "Native American*",
			value: "SUM_AMERINDSVC",
			total: "SUM_UA_AMERIND",
			amount: 0,
		},
		{
			displayName: "Asian*",
			value: "SUM_ASIAN_SVC",
			total: "SUM_UA_ASIAN",
			amount: 0,
		},
		{
			displayName: "Pacific Islander*",
			value: "SUM_PACIFICSVC",
			total: "SUM_UA_PACIFIC",
			amount: 0,
		},
		{
			displayName: "Other Race*",
			value: "SUM_OTHRACESVC",
			total: "SUM_UA_OTHRACE",
			amount: 0,
		},
		{
			displayName: "Two or More Races*",
			value: "SUM_RACE2UPSVC",
			total: "SUM_UA_RACE2UP",
			amount: 0,
		},
		{
			displayName: "Hispanic",
			value: "SUM_HISP_SVC",
			total: "SUM_UA_HISP",
			amount: 0,
		},
	];

	const ageGroupInServiceArea: any = [
		{
			displayName: "Youth (<20)",
			value: "SUM_KIDS_SVCA",
			total: "SUM_UA_KIDS",
			amount: 0,
		},
		{
			displayName: "Adults (20-64)",
			value: "SUM_YOUNG_PRO_SVCA",
			total: "SUM_UA_YOUNG_PRO",
			amount: 0,
		},
		{
			displayName: "Seniors (>64)",
			value: "SUM_SENIORS_SVCA",
			total: "SUM_UA_SENIORS",
			amount: 0,
		},
	];

	const incomeGroupInServiceArea: any = [
		{
			displayName: "Low Income (<75% MAI)",
			value: "SUM_HHILOWSVCA",
			total: "SUM_UA_TPLHHILOW",
			amount: 0,
		},
		{
			displayName: "Medium Income (75%-125% MAI)",
			value: "SUM_HHIMEDSVCA",
			total: "SUM_UA_TPLHHIMED",
			amount: 0,
		},
		{
			displayName: "High income (>125% MAI)",
			value: "SUM_HHIHGHSVCA",
			total: "SUM_UA_TPLHHIHGH",
			amount: 0,
		},
	];

	// The incomeData, ageData, and demoData state variables are used to store the respective datasets used to generate the charts
	const [incomeData, setIncomeData] = useState<any>({
		labels: incomeLabels,
		datasets: [
			{
				label: "income",
				data: [0, 0, 0],
				borderColor: colors,
				backgroundColor: colors,
			},
		],
	});
	const [demoData, setDemoData] = useState<any>({
		labels: demoLabels,
		datasets: [
			{
				label: "demographics",
				data: [0, 0, 0, 0, 0, 0, 0, 0],
				borderColor: colors,
				backgroundColor: colors,
			},
		],
	});

	const [ageData, setAgeData] = useState<any>({
		labels: ageLabels,
		datasets: [
			{
				label: "age",
				data: [0, 0, 0],
				borderColor: colors,
				backgroundColor: colors,
			},
		],
	});

	useEffect(() => {
		if (urbanarea) {
			setDemoData({
				labels: demoLabels,
				datasets: [
					{
						label: "",
						data: demographicsInServiceArea.map((x: any) =>
							urbanarea?.attributes[dataFields[x.value]] !== 0
								? Math.round(
									(urbanarea?.attributes[dataFields[x.value]] /
										urbanarea?.attributes[dataFields[x.total]]) *
											100
								  )
								: 0
						),
						borderColor: colors,
						backgroundColor: colors,
					},
				],
			});

			setAgeData({
				labels: ageLabels,
				datasets: [
					{
						label: "",
						data: ageGroupInServiceArea.map((x: any) =>
							urbanarea?.attributes[dataFields[x.value]] !== 0
								? Math.round(
									(urbanarea?.attributes[dataFields[x.value]] /
										urbanarea?.attributes[dataFields[x.total]]) *
											100
								  )
								: 0
						),
						borderColor: colors,
						backgroundColor: colors,
					},
				],
			});
			setIncomeData({
				labels: incomeLabels,
				datasets: [
					{
						label: "",
						data: incomeGroupInServiceArea.map((x: any) =>
							urbanarea?.attributes[x.value] !== 0
								? Math.round(
									(urbanarea?.attributes[dataFields[x.value]] /
										urbanarea?.attributes[dataFields[x.total]]) *
											100
								  )
								: 0
						),
						borderColor: colors,
						backgroundColor: colors,
					},
				],
			});
		}
	}, [urbanarea]);

	const convertAttributeFieldNames = (o: any) => {
		let fields = Object.keys(dataFields)
		let values = Object.values(dataFields)

		// update attribute fields to use SQL server names since hard-coded in python GP service  
		let stats: any = {}
		Object.keys(o).forEach((k) => {
			let key = k
			let i = values.indexOf(k)
			if (i >= 0) {
				key = fields[i]
			}
			stats[key] = o[k]
		})
		return stats
	}

	// This function submits the job and required parameters to the print service
	const submitJob = async () => {
		handleOpen();
		setGPSuccess(false);

		const newReport = urbanReport;
		// area table
		newReport.tables.Area.body.fields[0].value = Math.round(
			urban?.attributes[dataFields[newReport.tables.Population.body.fields[0].oname]]
		);
		// population table
		newReport.tables.Population.body.fields[0].value = Math.round(
			urban?.attributes[dataFields[newReport.tables.Population.body.fields[0].oname]]
		);
		// age table
		newReport.tables.Age.body.fields.forEach((field, i) => {
			newReport.tables.Age.body.fields[i].value = Math.round(
				urban?.attributes[dataFields[newReport.tables.Age.body.fields[i].oname]]
			);
		});
		// income table
		newReport.tables.Income.body.fields.forEach((field, i) => {
			newReport.tables.Income.body.fields[i].value = Math.round(
				urban?.attributes[dataFields[newReport.tables.Income.body.fields[i].oname]]
			);
		});
		// race table
		newReport.tables.Race.body.fields.forEach((field, i) => {
			newReport.tables.Race.body.fields[i].value = Math.round(
				urban?.attributes[dataFields[newReport.tables.Race.body.fields[i].oname]]
			);
		});

		// other parameters
		newReport.ProjectName = `${urban?.attributes[dataFields["NAME"]]} - Urban Level Report`;
		newReport.CityOrUrbanID = urban?.attributes[dataFields["UA_ID"]];

		//newReport.PlaceStats = city?.attributes;
		//newReport.UrbanStats = urban?.attributes;

		// update attribute fields to use SQL server names since hard-coded in python GP service 
		newReport.PlaceStats = convertAttributeFieldNames(city?.attributes);
		newReport.UrbanStats = convertAttributeFieldNames(urban?.attributes);

		const report_json = {
			report: newReport,
		};

		var gpParams = {
			INPUT_REPORT_JSON: JSON.stringify(report_json),
		};

		const jobInfo: JobInfo = await geoprocessor.submitJob(gpPDF_URL, gpParams);

		// The callback below monitors the progress of the GP Service to be communicated to the user
		const options = {
			interval: 3000,
			statusCallback: (j: any) => {
				if (j.messages.length) {
					setJobMessage(j.messages[j.messages.length - 1].description);
				} else {
					setJobMessage(
						"Unable to contact server.  Please wait or try again later."
					);
				}
				if (j.jobStatus === "esriJobFailed") {
					console.log("Error: Job failed.");
				}
			},
		};

		jobInfo.waitForJobCompletion(options).then((resp: any) => {
			getJobResultData(jobInfo);
		});

		// This function is triggered once the job is complete
		const getJobResultData = (jobInfo: JobInfo) => {
			jobInfo.fetchResultData("output_file").then((r: any) => {
				setGPSuccess(true);
				setPdfUrl(r.value.url);
			});
		};
	};

	return (
		<>
			<Typography
				sx={{ textAlign: "center" }}
				variant="h1"
				color="earth.main"
				gutterBottom
			>
				{urban?.attributes[dataFields["NAME"]]}
			</Typography>
			<Typography
				sx={{ textAlign: "center", fontFamily: "Helvetica Neue" }}
				variant="h5"
				color="earth.main"
				gutterBottom
			>
				Census-designated urban area
			</Typography>
			<Box
				alignItems="center"
				justifyContent="center"
				className="right-panel__export-button"
			>
				<Button
					aria-label="Export Access Statistics PDF"
					onClick={() => {
						trackEvent("export_statistics_click", "urban");
						submitJob();
					}}
					variant="contained"
				>
					Export Access Statistics PDF
				</Button>
			</Box>
			<Divider sx={{ marginTop: "25px" }} />
			<Box
				display="flex"
				flexDirection="row"
				justifyContent="center"
				sx={{ marginTop: "15px", marginBottom: "5px" }}
			>
				<Box
					display="flex"
					flexDirection="column"
					justifyContent="center"
					maxWidth="210px"
					textAlign="center"
				>
					<Typography
						variant="h1"
						color="green"
					>
						{Math.round(
							(urbanarea?.attributes[dataFields["SUM_TOTPOPSVCA"]] /
								urbanarea?.attributes[dataFields["SUM_TPL_TOT_POP"]]) *
								100
						) + "%"}
					</Typography>
					<Typography
						variant="h3"
						color=""
					>
						of residents live within a 10-minute walk of a park
					</Typography>
				</Box>
			</Box>

			<Box
				display="flex"
				flexDirection="row"
				justifyContent="space-between"
				alignItems="flex-end"
				sx={{ marginTop: "10px", marginBottom: "5px" }}
			>
				<Box
					display="flex"
					flexDirection="column"
					alignItems="center"
				>
					<Typography
						variant="subtitle2"
						color=""
					>
						Population Served
					</Typography>
					<Typography
						variant="h3"
						color=""
					>
						{urbanarea?.attributes[dataFields["SUM_TOTPOPSVCA"]].toLocaleString("en-US")}
					</Typography>
				</Box>
				<Box
					display="flex"
					flexDirection="column"
					alignItems="center"
				>
					<Typography
						variant="subtitle2"
						color=""
					>
						Total Population of Urban Area
					</Typography>
					<Typography
						variant="h3"
						color=""
					>
						{urbanarea?.attributes[dataFields["SUM_TPL_TOT_POP"]].toLocaleString("en-US")}
					</Typography>
				</Box>
			</Box>
			<Divider sx={{ marginTop: "25px" }} />
			<Typography
				variant="h5"
				color="grass.main"
				sx={{ marginBottom: "10px" }}
			>
				Race/Ethnicity
			</Typography>
			<Typography
				variant="subtitle2"
				color=""
			>
				Percentage of people within a 10-minute walk by race/ethnicity.
				*Excludes those that report Hispanic origin (which is captured
				separately from race by the U.S. Census).
			</Typography>
			<Divider />

			<Box
				height="400px"
				width="100%"
			>
				<Bar
					options={chartOptions(convertAttributeFieldNames(urbanarea?.attributes), demoData.labels)}
					plugins={[ChartDataLabels]}
					data={demoData}
				/>
			</Box>

			<Divider sx={{ marginTop: "25px" }} />
			<Typography
				variant="h5"
				color="grass.main"
				sx={{ marginBottom: "10px" }}
			>
				Age
			</Typography>
			<Typography
				variant="subtitle2"
				color=""
			>
				Percentage of people within a 10-minute walk by age
			</Typography>

			<Divider />
			<Box
				height="200px"
				width="100%"
			>
				<Bar
					options={chartOptions(convertAttributeFieldNames(urbanarea?.attributes), ageData.labels)}
					plugins={[ChartDataLabels]}
					data={ageData}
				/>
			</Box>

			<Divider sx={{ marginTop: "25px" }} />
			<Typography
				variant="h5"
				color="grass.main"
				sx={{ marginBottom: "10px" }}
			>
				Income
			</Typography>
			<Typography
				variant="subtitle2"
				color=""
			>
				Percentage of households within a 10-minute walk of a park by income.
				MAI = Median Area Income, which is the median income of the urban area.
			</Typography>

			<Divider />
			<Box
				height="200px"
				width="100%"
				id="urban_area_scroll"
			>
				<Bar
					options={chartOptions(convertAttributeFieldNames(urbanarea?.attributes), incomeData.labels)}
					plugins={[ChartDataLabels]}
					data={incomeData}
				/>
			</Box>

			<Divider sx={{ marginTop: "25px" }} />

			<Typography
				textAlign="left"
				variant="h6"
				color="headingGrey.main"
			>
				Demographic information sourced from Esri 2025
			</Typography>

			<Modal
				open={open}
				onClose={handleClose}
				aria-labelledby="modal-modal-title"
				aria-describedby="modal-modal-description"
			>
				<Box sx={modalStyle}>
					<IconButton
						aria-label="close"
						onClick={handleClose}
						sx={{
							position: "absolute",
							right: 8,
							top: 8,
							color: (theme) => theme.palette.grey[500],
						}}
					>
						<CloseIcon />
					</IconButton>

					{!gpSuccess && (
						<>
							<Typography
								id="modal-modal-title"
								variant="h3"
								component="h2"
							>
								Generating PDF report...
							</Typography>
							<Typography
								id="modal-modal-description"
								sx={{ mt: 2 }}
							>
								{jobMessage}
							</Typography>
						</>
					)}
					{gpSuccess && (
						<>
							<Typography
								id="modal-modal-title"
								variant="h3"
								component="h2"
							>
								Success!
							</Typography>
							<Button
								variant="contained"
								sx={{ marginTop: "10px" }}
								href={pdfUrl}
								target="_blank"
								onClick={() => {
									trackEvent("export_statistics_pdf", "urban");
								}}
							>
								Download PDF Report
							</Button>
						</>
					)}
				</Box>
			</Modal>
		</>
	);
}
